import { useState, type FormEvent } from 'react'
import { Cargando } from '../../componentes/Cargando'
import { EstadoError } from '../../componentes/EstadoError'
import { AvisoError, Campo, CLASE_BOTON, CLASE_BOTON_PRIMARIO, CLASE_INPUT } from '../../componentes/formulario'
import { euros } from '../../componentes/formato'
import { useCatalogo } from '../../hooks/useCatalogo'
import { useFijarPrecio, usePreciosCliente, useQuitarPrecio } from '../../hooks/useGestionClientes'
import { BarraPaginacion } from '../../componentes/BarraPaginacion'
import { usePaginacionLocal } from '../../componentes/paginacion'

/**
 * Precios pactados del cliente (RF-42). Sin precio pactado, el pedido toma el
 * del último albarán y, si no hay, el del catálogo. Fijar y quitar es de admin.
 */
export function PreciosCliente({ contactId, admin }: { contactId: string; admin: boolean }) {
  const precios = usePreciosCliente(contactId)
  const catalogo = useCatalogo(false)
  const fijar = useFijarPrecio(contactId)
  const quitar = useQuitarPrecio(contactId)
  const [producto, setProducto] = useState('')
  const [precio, setPrecio] = useState('')
  const [aviso, setAviso] = useState<string | null>(null)

  const enviar = (e: FormEvent) => {
    e.preventDefault()
    const valor = Number(precio.replace(',', '.'))
    if (!producto) return setAviso('Elige un producto.')
    if (precio.trim() === '' || !Number.isFinite(valor) || valor < 0) return setAviso('El precio tiene que ser un número mayor o igual que 0.')
    setAviso(null)
    fijar.mutate({ productId: producto, precio: valor }, { onSuccess: () => { setPrecio(''); setProducto('') } })
  }

  const items = precios.data?.items ?? []
  const pag = usePaginacionLocal(items, contactId)

  return (
    <div className="border-t border-borde-suave pt-4">
      <h3 className="text-sm font-semibold text-tinta">Precios pactados</h3>
      <p className="mt-1 text-xs text-tinta-apagada">
        Sin precio pactado, el pedido usa el del último albarán y, si no hay, el del catálogo. Precios sin impuesto.
      </p>
      {precios.isPending ? (
        <Cargando mensaje="Cargando los precios" />
      ) : precios.isError ? (
        <div className="mt-2"><EstadoError error={precios.error} onReintentar={() => void precios.refetch()} /></div>
      ) : items.length === 0 ? (
        <p className="mt-2 text-sm text-tinta-2">Este cliente no tiene precios pactados.</p>
      ) : (
        <ul aria-label="Precios pactados" className="mt-2 flex flex-col divide-y divide-borde-suave rounded-lg border border-borde">
          {pag.visibles.map((p) => (
            <li key={p.product_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
              <span className="min-w-0 truncate text-tinta">
                {p.nombre}
                {!p.activo ? <span className="ml-2 text-xs text-tinta-apagada">(producto desactivado)</span> : null}
              </span>
              <span className="flex items-center gap-3">
                <span className="tabular-nums text-tinta-2">{euros(p.precio)}</span>
                {admin ? (
                  <button
                    type="button"
                    disabled={quitar.isPending}
                    onClick={() => quitar.mutate({ productId: p.product_id })}
                    className="rounded-md px-2 py-1 text-xs font-medium text-tinta-2 hover:bg-elevado"
                    aria-label={`Quitar el precio de ${p.nombre}`}
                  >
                    Quitar
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {pag.paginas > 1 ? (
        <div className="mt-2">
          <BarraPaginacion pagina={pag.pagina} paginas={pag.paginas} onCambiar={pag.irAPagina} etiqueta="Paginación de los precios" />
        </div>
      ) : null}

      {admin ? (
        <form onSubmit={enviar} className="mt-3 grid gap-2 sm:grid-cols-[1fr_8rem_auto] sm:items-end" noValidate>
          <Campo etiqueta="Producto">
            <select value={producto} onChange={(e) => setProducto(e.target.value)} className={CLASE_INPUT}>
              <option value="">Elige un producto</option>
              {(catalogo.data?.items ?? []).filter((p) => p.activo).map((p) => (
                <option key={p.id} value={p.id}>{`${p.nombre} (${euros(p.precio)})`}</option>
              ))}
            </select>
          </Campo>
          <Campo etiqueta="Precio pactado">
            <input value={precio} onChange={(e) => setPrecio(e.target.value)} inputMode="decimal" className={CLASE_INPUT} />
          </Campo>
          <button type="submit" disabled={fijar.isPending} className={CLASE_BOTON_PRIMARIO}>
            {fijar.isPending ? 'Guardando' : 'Fijar precio'}
          </button>
        </form>
      ) : (
        <p className="mt-2 text-xs text-tinta-apagada">Solo un administrador puede cambiar los precios pactados.</p>
      )}
      {aviso ? <p role="alert" className="mt-2 text-sm text-atencion-tinta">{aviso}</p> : null}
      <div className="mt-2"><AvisoError error={fijar.error ?? quitar.error} /></div>
      {catalogo.isError && admin ? (
        <p className="mt-2 text-xs text-tinta-apagada">
          No se ha podido cargar el catálogo. <button type="button" className={CLASE_BOTON} onClick={() => void catalogo.refetch()}>Reintentar</button>
        </p>
      ) : null}
    </div>
  )
}
